import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Card,
  CardHeader,
  Container,
  Row,
  Col,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';
import Header from 'components/Headers/Header';
import Swal from 'sweetalert2';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getCollectionNameForSpecialty, getSpecialtyStorageFolder } from '../../util/specialtyCollections';

const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_COLOR = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  closed: 'secondary',
};
const STATUS_LABEL = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const ManageReportedIssuesPage = () => {
  const activeSpecialty = useSelector((state) => state.specialty?.activeSpecialty || 'pmr');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [userCache, setUserCache] = useState({});

  const collName = getCollectionNameForSpecialty('reportedIssues', activeSpecialty);

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, collName),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setIssues(data);
      setLoading(false);
    }, () => {
      setLoading(false);
    });

    return () => unsubscribe();
  }, [collName]);

  // Fetch user details (name, email) for all unique userIds
  useEffect(() => {
    const userIds = [...new Set(issues.map(i => i.userId).filter(Boolean))];
    const missing = userIds.filter(id => !userCache[id]);
    if (missing.length === 0) return;

    const fetchUsers = async () => {
      const updates = {};
      await Promise.all(missing.map(async (uid) => {
        try {
          const q = query(collection(db, 'users'), where('userId', '==', uid));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const d = snap.docs[0].data();
            updates[uid] = { name: d.name || '', email: d.email || '' };
          } else {
            updates[uid] = { name: '', email: '' };
          }
        } catch {
          updates[uid] = { name: '', email: '' };
        }
      }));
      setUserCache(prev => ({ ...prev, ...updates }));
    };
    fetchUsers();
  }, [issues]);

  const specialtyIssues = issues;

  const filteredIssues = specialtyIssues.filter(issue => {
    const matchesStatus = statusFilter === 'all' || (issue.status || 'open') === statusFilter;
    const user = userCache[issue.userId] || {};
    const matchesSearch = !searchTerm ||
      (issue.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.userId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
  };

  const getFileUrl = (filename, issueSpecialty) => {
    if (!filename) return null;
    const folder = getSpecialtyStorageFolder('files-reported-issues', issueSpecialty || activeSpecialty);
    return `https://storage.googleapis.com/pmr-bank.firebasestorage.app/${folder}/${filename}`;
  };

  const handleStatusChange = async (issueId, newStatus) => {
    setUpdatingId(issueId);
    try {
      await updateDoc(doc(db, collName, issueId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      if (selectedIssue?.id === issueId) {
        setSelectedIssue(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to update status.', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const statusCounts = specialtyIssues.reduce((acc, issue) => {
    const s = issue.status || 'open';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center mb-3">
                  <Col md="6">
                    <h3 className="mb-0">Reported Issues ({filteredIssues.length})</h3>
                  </Col>
                  <Col md="6">
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="fas fa-search"></i>
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="text"
                        placeholder="Search by title or user..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {['all', ...STATUS_OPTIONS].map(s => (
                      <button
                        key={s}
                        className={`btn btn-sm mr-2 mb-1 ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setStatusFilter(s)}
                      >
                        {s === 'all' ? 'All' : STATUS_LABEL[s]}
                        <Badge color="light" className="ml-2 text-dark">
                          {s === 'all' ? specialtyIssues.length : (statusCounts[s] || 0)}
                        </Badge>
                      </button>
                    ))}
                  </Col>
                </Row>
              </CardHeader>

              {loading && (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Loading issues...</p>
                </div>
              )}

              {!loading && filteredIssues.length === 0 && (
                <div className="text-center py-5">
                  <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                  <h4 className="text-muted">No Issues Found</h4>
                  <p className="text-muted">
                    {searchTerm || statusFilter !== 'all'
                      ? 'Try adjusting your filters.'
                      : 'No issues have been reported yet.'}
                  </p>
                </div>
              )}

              {!loading && filteredIssues.length > 0 && (
                <div className="table-responsive">
                  <table className="table align-items-center table-flush">
                    <thead className="thead-light">
                      <tr>
                        <th>Title</th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Date Submitted</th>
                        <th>Attachment</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIssues.map(issue => (
                        <tr key={issue.id}>
                          <td>
                            <span className="font-weight-bold">{issue.title}</span>
                          </td>
                          <td>
                            {userCache[issue.userId] ? (
                              <>
                                <span className="font-weight-bold">{userCache[issue.userId].name || issue.userId}</span>
                                <br />
                                <small className="text-muted">{userCache[issue.userId].email}</small>
                              </>
                            ) : (
                              <small className="text-muted">{issue.userId || '—'}</small>
                            )}
                          </td>
                          <td>
                            <Input
                              type="select"
                              bsSize="sm"
                              style={{ width: 'auto', display: 'inline-block' }}
                              value={issue.status || 'open'}
                              disabled={updatingId === issue.id}
                              onChange={(e) => handleStatusChange(issue.id, e.target.value)}
                            >
                              {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                              ))}
                            </Input>
                          </td>
                          <td>
                            <small>{formatDate(issue.createdAt)}</small>
                          </td>
                          <td>
                            {issue.filename ? (
                              <a
                                href={getFileUrl(issue.filename)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary"
                              >
                                <i className="fas fa-paperclip mr-1"></i>
                                View
                              </a>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td className="text-right">
                            <Button
                              size="sm"
                              color="secondary"
                              onClick={() => setSelectedIssue(issue)}
                            >
                              <i className="fas fa-eye mr-1"></i>
                              Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </Row>
      </Container>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedIssue} toggle={() => setSelectedIssue(null)} size="lg">
        <ModalHeader toggle={() => setSelectedIssue(null)}>
          Issue Details
        </ModalHeader>
        <ModalBody>
          {selectedIssue && (
            <>
              <dl className="row">
                <dt className="col-sm-3">Title</dt>
                <dd className="col-sm-9"><strong>{selectedIssue.title}</strong></dd>

                <dt className="col-sm-3">User</dt>
                <dd className="col-sm-9">
                  {userCache[selectedIssue.userId] ? (
                    <>
                      <span className="font-weight-bold">{userCache[selectedIssue.userId].name || selectedIssue.userId}</span>
                      <br />
                      <small className="text-muted">{userCache[selectedIssue.userId].email}</small>
                    </>
                  ) : (
                    <small className="text-muted">{selectedIssue.userId || '—'}</small>
                  )}
                </dd>

                <dt className="col-sm-3">Submitted</dt>
                <dd className="col-sm-9">{formatDate(selectedIssue.createdAt)}</dd>

                <dt className="col-sm-3">Status</dt>
                <dd className="col-sm-9">
                  <FormGroup className="mb-0">
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ width: 'auto' }}
                      value={selectedIssue.status || 'open'}
                      disabled={updatingId === selectedIssue.id}
                      onChange={(e) => handleStatusChange(selectedIssue.id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </dd>

                <dt className="col-sm-3">Description</dt>
                <dd className="col-sm-9" style={{ whiteSpace: 'pre-wrap' }}>{selectedIssue.body}</dd>
              </dl>

              {selectedIssue.filename && (
                <div className="mt-3">
                  <a
                    href={getFileUrl(selectedIssue.filename)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-primary"
                  >
                    <i className="fas fa-paperclip mr-2"></i>
                    View Attachment
                  </a>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSelectedIssue(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ManageReportedIssuesPage;
